import React from 'react';
import type { ExploreFilters } from '../../services/exploreApi';
import './ExploreSurface.css';

/**
 * §1.8's chip row: the only view filters, and the only fields plans can answer
 * truthfully today.
 *
 * **Real radio inputs in a fieldset, not buttons with `aria-pressed`.** Each
 * dimension is single-select, which is what a radio group IS — so the browser
 * gives arrow-key navigation, roving focus, the group name spoken with each
 * option, and the selected-of-N count, none of which has to be reimplemented or
 * can drift. §3 makes accessibility a guarantee here rather than a follow-up,
 * and the cheapest way to keep a guarantee is to not hand-roll it.
 *
 * The VALUES are the server's vocabulary verbatim (`explore_filters.py`), and a
 * test binds them to it. A chip carrying a value the server does not recognise
 * is refused outright — and §1.8 spells out why that matters more than it
 * looks: an unknown filter "matches NO plan, which zeroes every count and
 * quietly degrades every numbered pin to a guide pin or hides it. A typo'd chip
 * would redraw the whole map and say nothing."
 */
export const LENGTH_OPTIONS = [
  { value: 'any', label: 'Any length' },
  { value: 'day', label: 'A day' },
  { value: 'weekend', label: 'A weekend' },
  { value: 'week', label: 'A week+' },
] as const;

export const GROUP_OPTIONS = [
  { value: 'any', label: 'Anyone' },
  { value: 'solo', label: 'Solo' },
  { value: 'couple', label: 'Couple' },
  { value: 'family', label: 'Family' },
  { value: 'friends', label: 'Friends' },
  { value: 'group', label: 'Group' },
] as const;

/**
 * The band VALUES are the stored words, never the glyphs — `budget.py` is
 * explicit that a glyph is display only, and the server refuses `?band=$$`.
 * The labels pair the glyph with its word so the chip is legible to a screen
 * reader ("$$" alone reads as "dollar dollar") and to anyone who has not met
 * the Yelp convention.
 */
export const BAND_OPTIONS = [
  { value: 'any', label: 'Any budget' },
  { value: 'economical', label: '$ Economical' },
  { value: 'mid', label: '$$ Mid-range' },
  { value: 'luxury', label: '$$$ Luxury' },
] as const;

/**
 * The one sentence the band chips may not stand without (owner decision,
 * Sep 7): bands are RELATIVE TO THE DESTINATION — `$$` is mid-range for that
 * place, the Yelp convention — so a chip read as a spending cap is "a lie the
 * filter cannot keep". A `$` week in Tokyo still costs more than `$$$` in
 * rural Portugal; the filter answers HOW AM I TRAVELLING, not what it costs.
 */
export const BAND_NOTE = 'Relative to the destination — how you travel there, not what it costs.';

const Chips: React.FC<{
  name: string;
  legend: string;
  options: readonly { value: string; label: string }[];
  value: string;
  onChange: (value: string) => void;
  /** A qualifier the whole set needs — rendered INSIDE the fieldset and named
   *  to it, so assistive tech reads it with the group rather than finding it
   *  as stray prose after the chips. */
  note?: string;
}> = ({ name, legend, options, value, onChange, note }) => (
  <fieldset
    className="explore-filters__set"
    aria-describedby={note ? `${name}-note` : undefined}
  >
    <legend className="explore-filters__legend">{legend}</legend>
    {options.map((option) => (
      <label
        key={option.value}
        className={`explore-chip${value === option.value ? ' explore-chip--on' : ''}`}
      >
        <input
          type="radio"
          name={name}
          value={option.value}
          checked={value === option.value}
          onChange={() => onChange(option.value)}
        />
        {option.label}
      </label>
    ))}
    {note && <p id={`${name}-note`} className="explore-filters__note">{note}</p>}
  </fieldset>
);

export const ViewFilters: React.FC<{
  filters: ExploreFilters;
  onChange: (filters: ExploreFilters) => void;
}> = ({ filters, onChange }) => (
  // `aria-label`, because a screen reader meeting two fieldsets in a row needs
  // to know they belong to one control before it meets the first legend.
  <div className="explore-filters" role="group" aria-label="Filter what is in view">
    <Chips
      name="explore-length"
      legend="Length"
      options={LENGTH_OPTIONS}
      value={filters.length ?? 'any'}
      onChange={(length) => onChange({ ...filters, length })}
    />
    <Chips
      name="explore-group"
      legend="Who it is for"
      options={GROUP_OPTIONS}
      value={filters.group ?? 'any'}
      onChange={(group) => onChange({ ...filters, group })}
    />
    <Chips
      name="explore-band"
      legend="Budget"
      options={BAND_OPTIONS}
      value={filters.band ?? 'any'}
      onChange={(band) => onChange({ ...filters, band })}
      note={BAND_NOTE}
    />
  </div>
);
